import React, { useCallback, useEffect, useState } from 'react'
import { useSnapshot } from 'valtio'
import { miscUiState } from '../../globalState'
import { fetchUtopiaAccountStats, type UtopiaAccountStats } from '../../api/utopiaAccountStatsApi'
import { fetchRconStats, formatStatsDate, type RconStats } from '../../api/rconStatsApi'
import { utopiaAuthState } from './utopiaAuthState'
import styles from './utopiaLanding.module.css'

export default function UtopiaStats () {
  const { appConfig } = useSnapshot(miscUiState)
  const { user } = useSnapshot(utopiaAuthState)
  const publicAccountStats = appConfig?.publicAccountStats === true

  const [stats, setStats] = useState<RconStats | null>(null)
  const [accountStats, setAccountStats] = useState<UtopiaAccountStats | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [accountError, setAccountError] = useState<string | null>(null)
  const [accountAuthRequired, setAccountAuthRequired] = useState(false)

  const load = useCallback(async (signal?: AbortSignal) => {
    setLoading(true)
    setError(null)
    setAccountError(null)
    setAccountAuthRequired(false)

    const rconPromise = fetchRconStats(signal)
    const accountsPromise = (publicAccountStats || user)
      ? fetchUtopiaAccountStats(signal)
      : Promise.resolve(null)

    const [rconData, accountsData] = await Promise.all([rconPromise, accountsPromise])
    if (signal?.aborted) return
    if (!publicAccountStats && !user) {
      setAccountAuthRequired(true)
    } else if (accountsData?.error) {
      if (accountsData.error.includes('Connexion requise')) {
        setAccountAuthRequired(true)
      } else {
        setAccountError(accountsData.error)
      }
      setAccountStats(accountsData)
    } else if (accountsData) {
      setAccountStats(accountsData)
    }
    setStats(rconData)
    setError(rconData?.error ?? null)
    setLoading(false)
  }, [publicAccountStats, user])

  useEffect(() => {
    const ac = new AbortController()
    void load(ac.signal)
    const interval = setInterval(() => {
      void load()
    }, 10_000)
    return () => {
      ac.abort()
      clearInterval(interval)
    }
  }, [load])

  const refresh = () => {
    void load()
  }

  return (
    <div className={styles.pagePanel}>
      <header className={styles.statsHeader}>
        <div>
          <h2 className={styles.joinTitle}>Statistiques Utopie</h2>
          <p className={styles.statsSubtitle}>
            Serveur et comptes Utopie
            {(accountStats?.updatedAt ?? stats?.updatedAt) ? (
              <span className={styles.statsUpdated}>
                {' '}
                · MAJ {formatStatsDate(accountStats?.updatedAt ?? stats?.updatedAt)}
              </span>
            ) : null}
          </p>
        </div>
        <button
          type="button"
          className={styles.statsRefresh}
          onClick={refresh}
          disabled={loading}
        >
          Actualiser
        </button>
      </header>

      {loading && !stats && !accountStats ? (
        <p className={styles.statsLoading}>Chargement…</p>
      ) : null}
      {accountAuthRequired ? (
        <p className={styles.playHint}>
          Connectez-vous via le menu en haut à droite pour afficher les statistiques des comptes Utopie.
        </p>
      ) : null}
      {accountError ? (
        <p className={styles.statsError}>{accountError}</p>
      ) : null}
      {error && !stats?.online ? (
        <p className={styles.statsError}>{error}</p>
      ) : null}

      {accountStats && !accountStats.error ? (
        <section className={styles.statsSection}>
          <h3 className={styles.joinSectionTitle}>Comptes Utopie</h3>
          <div className={styles.statsGrid}>
            <div className={styles.statCard}>
              <span className={styles.statLabel}>Comptes Utopie</span>
              <span className={styles.statValue}>{accountStats.totalAccounts}</span>
            </div>
            <div className={styles.statCard}>
              <span className={styles.statLabel}>Liés à Microsoft</span>
              <span className={styles.statValue}>{accountStats.microsoftAccounts}</span>
            </div>
            <div className={styles.statCard}>
              <span className={styles.statLabel}>Non liés (sans Mojang)</span>
              <span className={styles.statValue}>{accountStats.nonMicrosoftAccounts}</span>
            </div>
          </div>
        </section>
      ) : null}

      {stats?.online ? (
        <section className={styles.statsSection}>
          <h3 className={styles.joinSectionTitle}>Serveur en direct</h3>
          <div className={styles.statsGrid}>
            <div className={`${styles.statCard} ${styles.statCardHighlight}`}>
              <span className={styles.statLabel}>Statut</span>
              <span className={styles.statValue}>
                <span className={styles.statOnline}>En ligne</span>
              </span>
            </div>
            <div className={styles.statCard}>
              <span className={styles.statLabel}>Joueurs connectés</span>
              <span className={styles.statValue}>
                {stats.playersOnline ?? 0}
                <span className={styles.statMuted}>
                  {' '}
                  / {stats.playersMax ?? '—'}
                </span>
              </span>
            </div>
            <div className={styles.statCard}>
              <span className={styles.statLabel}>Version</span>
              <span className={styles.statValue}>Toutes les versions</span>
            </div>
          </div>

          {stats.playersListRestricted && !user ? (
            <p className={styles.playHint}>
              Connectez-vous pour afficher la liste des joueurs en ligne.
            </p>
          ) : (
            <div className={styles.playersPanel}>
              <h4 className={styles.playersPanelTitle}>
                Joueurs en ligne ({stats.players?.length ?? stats.playersOnline ?? 0})
              </h4>
              {!stats.players?.length ? (
                <p className={styles.playersEmpty}>Aucun joueur connecté pour le moment.</p>
              ) : (
                <ul className={styles.playersList}>
                  {stats.players.map(name => (
                    <li key={name} className={styles.playerChip}>{name}</li>
                  ))}
                </ul>
              )}
            </div>
          )}
        </section>
      ) : stats && !stats.online ? (
        <section className={styles.statsSection}>
          <div className={styles.statsGrid}>
            <div className={`${styles.statCard} ${styles.statCardHighlight}`}>
              <span className={styles.statLabel}>Statut</span>
              <span className={styles.statValue}>
                <span className={styles.statOffline}>Hors ligne</span>
              </span>
            </div>
          </div>
        </section>
      ) : null}
    </div>
  )
}
